import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Upload, Clock, CheckCircle, XCircle, MessageSquare,
  ChevronRight, Filter, Search, RefreshCw, Download, Eye,
  AlertTriangle, Star, Calendar, User
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * MY SUBMISSIONS & FEEDBACK PAGE
 * Shows mentee's uploaded assignments and mentor feedback
 */

const MySubmissions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, reviewed, approved, rejected
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('access_token');
  
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/submissions/my-submissions`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
      } else {
        toast.error('Failed to load submissions');
      }
    } catch (e) {
      toast.error('Error loading submissions');
    } finally {
      setLoading(false);
    }
  };

  const filteredSubmissions = submissions.filter(s => {
    if (filter === 'all') return true;
    return s.status === filter;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      reviewed: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      reviewed: <MessageSquare className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      rejected: <XCircle className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {icons[status]}
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Submissions & Feedback</h1>
              <p className="text-gray-500 mt-1">Track your homework and assignment submissions</p>
            </div>
            <button
              onClick={() => navigate('/assignments')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              data-testid="upload-new-btn"
            >
              <Upload className="w-4 h-4" />
              Upload New
            </button>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex gap-2 mt-6 overflow-x-auto">
            {['all', 'pending', 'reviewed', 'approved', 'rejected'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  filter === f 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`filter-${f}`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f !== 'all' && (
                  <span className="ml-2 bg-white/20 px-1.5 py-0.5 rounded-full text-xs">
                    {submissions.filter(s => s.status === f).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {filteredSubmissions.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No submissions yet</h3>
            <p className="text-gray-500 mb-6">Upload your first assignment to get started</p>
            <button
              onClick={() => navigate('/assignments')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg inline-flex items-center gap-2"
            >
              <Upload className="w-5 h-5" />
              Upload Assignment
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSubmissions.map((submission) => (
              <div 
                key={submission.id} 
                className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow"
                data-testid={`submission-${submission.id}`}
              >
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      submission.status === 'approved' ? 'bg-green-100' :
                      submission.status === 'rejected' ? 'bg-red-100' :
                      submission.status === 'reviewed' ? 'bg-blue-100' :
                      'bg-yellow-100'
                    }`}>
                      <FileText className={`w-6 h-6 ${
                        submission.status === 'approved' ? 'text-green-600' :
                        submission.status === 'rejected' ? 'text-red-600' :
                        submission.status === 'reviewed' ? 'text-blue-600' :
                        'text-yellow-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{submission.title || submission.file_name}</h3>
                        {getStatusBadge(submission.status)}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {submission.assignment_title || submission.category || 'General Submission'}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown'}
                        </span>
                        {submission.reviewed_at && (
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Reviewed: {new Date(submission.reviewed_at).toLocaleDateString('en-GB')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 sm:flex-shrink-0">
                    <button
                      onClick={() => setSelectedSubmission(submission)}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium flex items-center gap-2"
                      data-testid={`view-${submission.id}`}
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </button>
                  </div>
                </div>
                
                {/* Feedback Preview */}
                {submission.feedback && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1 bg-purple-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-purple-900">Mentor Feedback</span>
                          {submission.grade && (
                            <span className="flex items-center gap-1 text-sm font-bold text-purple-600">
                              <Star className="w-4 h-4 fill-purple-600" />
                              {submission.grade}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-purple-800 line-clamp-2">{submission.feedback}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submission Detail Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b bg-gray-50">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedSubmission.title || selectedSubmission.file_name}</h2>
                  <p className="text-gray-500 mt-1">{selectedSubmission.assignment_title || 'Submission Details'}</p>
                </div>
                <button 
                  onClick={() => setSelectedSubmission(null)}
                  className="p-2 hover:bg-gray-200 rounded-lg"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Status and Dates */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  {getStatusBadge(selectedSubmission.status)}
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Submitted</p>
                  <p className="font-medium">
                    {selectedSubmission.submitted_at ? new Date(selectedSubmission.submitted_at).toLocaleString('en-GB') : 'Unknown'}
                  </p>
                </div>
                {selectedSubmission.reviewed_at && (
                  <>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Reviewed</p>
                      <p className="font-medium">
                        {new Date(selectedSubmission.reviewed_at).toLocaleString('en-GB')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Reviewed By</p>
                      <p className="font-medium">{selectedSubmission.reviewed_by || 'Mentor'}</p>
                    </div>
                  </>
                )}
              </div>
              
              {/* Grade */}
              {selectedSubmission.grade && (
                <div className="mb-6 p-4 bg-gradient-to-r from-purple-500 to-orange-500 rounded-xl text-white">
                  <p className="text-sm opacity-80 mb-1">Your Grade</p>
                  <div className="flex items-center gap-2">
                    <Star className="w-8 h-8 fill-white" />
                    <span className="text-3xl font-bold">{selectedSubmission.grade}</span>
                  </div>
                </div>
              )}
              
              {/* Feedback */}
              {selectedSubmission.feedback ? (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-purple-600" />
                    Mentor Feedback
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedSubmission.feedback}</p>
                  </div>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                  <div className="flex items-center gap-2 text-yellow-700">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">Awaiting Feedback</span>
                  </div>
                  <p className="text-yellow-600 text-sm mt-1">Your mentor will review this submission soon.</p>
                </div>
              )}
              
              {/* File Info */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Submitted File</h3>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">{selectedSubmission.file_name}</p>
                      <p className="text-sm text-gray-500">{selectedSubmission.file_type || 'Document'}</p>
                    </div>
                  </div>
                  {selectedSubmission.file_url && (
                    <a
                      href={selectedSubmission.file_url.startsWith('http') ? selectedSubmission.file_url : `${API_URL}${selectedSubmission.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </a>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setSelectedSubmission(null)}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySubmissions;
